import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { AddressService } from '../property/address.service';
import { PropertyAssessmentService } from '../property/property-assessment.service';
import { UserProfileService } from '../user-profile.service';
import { CaseService } from '../case.service';
import { SigningService } from '../signing/signing.service';
import type {
  SendOtpResponse,
  VerifyOtpResponse,
  ProgressResponse,
} from '@kin/property-tax-types';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private http = inject(HttpClient);
  private addressService = inject(AddressService);
  private assessmentService = inject(PropertyAssessmentService);
  private profileService = inject(UserProfileService);
  private caseService = inject(CaseService);
  private signingService = inject(SigningService);

  private readonly TOKEN_KEY = 'kin_auth_token';

  sendOtp(email: string): Observable<SendOtpResponse> {
    return this.http.post<SendOtpResponse>(`${environment.apiUrl}/auth/send-otp`, { email });
  }

  verifyOtp(email: string, code: string): Observable<VerifyOtpResponse> {
    return this.http
      .post<VerifyOtpResponse>(`${environment.apiUrl}/auth/verify-otp`, { email, code })
      .pipe(tap(res => this.setToken(res.access_token)));
  }

  getProgress(): Observable<ProgressResponse> {
    return this.http.get<ProgressResponse>(`${environment.apiUrl}/user/progress`);
  }

  updateProgress(step: string): Observable<ProgressResponse> {
    return this.http.put<ProgressResponse>(`${environment.apiUrl}/user/progress`, { step });
  }

  isLoggedIn(): boolean {
    return !!this.getToken();
  }

  isAdmin(): boolean {
    const token = this.getToken();
    if (!token) return false;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.isAdmin === true || payload.role === 'admin';
    } catch {
      return false;
    }
  }

  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  private setToken(token: string): void {
    localStorage.setItem(this.TOKEN_KEY, token);
  }

  logout(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    this.addressService.clear();
    this.assessmentService.clear();
    this.profileService.clear();
    this.caseService.clear();
    this.signingService.clear();
  }
}
